// The phone keeps one navigation row. The existing dialog, launcher tiles,
// account menu and workspace switcher own their behavior and accessibility
// contracts.
export const PhoneNavigation = {
  display: 'contents',
  canCompose: true,
  dataPhoneNav: (el, s) => (el.call('phoneNavigationAvailable', s.root) ? 'true' : null),
  subscribeTo: [
    'workspace',
    'currentPath',
    '_canvasSlot',
    '_canvasSlotSecond',
    'workspaceProject',
    '_projectCanvasOpen',
    'data',
    '_moduleChromePref',
    '_moduleComposerHidden',
    'assistantOpen',
    'assistantDock',
    'voiceOpen',
    'voiceHost'
  ],
  onMounted: (el, s) => el.call('phoneNavigationSync', el, s),
  onUpdate: (el, s) => el.call('phoneNavigationSync', el, s),
  onWindowResize: (e, el, s) => el.call('phoneNavigationSync', el, s),

  // Back on a full-page app route (Nika 2026-10-31, POB S11c2 option B): an
  // installed app opened from Apps is a page, not a pane, and the phone folds
  // the header away — the dock was its only way out. The pane header's own
  // Back (canvas components/PageSlot.js _histBtn): the same button, glyph and
  // words. Hidden here; the phone rule in functions/phoneNavigation.js
  // (phoneNavigationStyles) shows it on an 'app' route with no pane open and
  // keeps its row clear of the page.
  PhoneBack: {
    extends: 'PageSlotHeaderBtn',
    dataPhoneBack: true,
    display: 'none',
    position: 'fixed',
    top: 'var(--slot-top)',
    left: 'calc(var(--spacing-Z) + env(safe-area-inset-left))',
    zIndex: '1100',
    ':active': { background: 'selected', color: 'title' },
    ariaLabel: (el) => el.call('polyglot', 'canvas.pageSlot.back'),
    title: (el) => el.call('polyglot', 'canvas.pageSlot.back'),
    Icon: { name: 'chevronLeft' },
    onClick: (e, el) => {
      e.preventDefault()
      el.call('phoneNavigationBack')
    }
  },

  Dock: {
    hide: (el, s) => !el.call('phoneNavigationAvailable', s.root),
    display: 'none',
    '@mobileL': { display: 'flex' },
    tag: 'nav',
    ariaLabel: (el) => el.call('polyglot', 'shell.headerTabs.primaryNav'),
    dataPhoneDock: true,
    position: 'fixed',
    left: 'calc(var(--spacing-Z) + env(safe-area-inset-left))',
    right: 'calc(var(--spacing-Z) + env(safe-area-inset-right))',
    bottom: 'calc(var(--spacing-Z) + env(safe-area-inset-bottom))',
    maxWidth: 'overlayLg',
    margin: '0 auto',
    zIndex: '1100',
    flow: 'x',
    gap: 'X',
    padding: 'X',
    theme: 'common-box',
    shadow: 'float',
    round: 'radiusSheet',
    borderWidth: 'hairline',
    borderStyle: 'solid',
    borderColor: 'line',
    boxSizing: 'border-box',
    PhoneNavButton_home: {
      icon: 'home',
      label: 'shell.app.home',
      ariaCurrent: (el, s) =>
        el.call('stripWorkspacePrefix', s.root.currentPath || '/').split(/[?#]/)[0] === '/'
          ? 'page'
          : null,
      // A fresh Fly on the home in Fly mode, like the header's logo
      // (functions/assistantFlyWorkspace.js assistantFlyHomeFresh); the dock
      // navigates itself.
      onClick: (e, el) => {
        el.call('assistantFlyHomeFresh', { navigate: false })
        el.call('phoneNavigationOpen', '/')
      }
    },
    PhoneNavButton_apps: {
      icon: 'grid',
      // The dock's own short name: the header's `apps` reads "აპლიკაციები",
      // which takes two lines in a 320px dock.
      label: 'shell.phoneNav.apps',
      ariaHaspopup: 'dialog',
      ariaExpanded: (el, s) => (s.root._phoneNavPanel === 'apps' ? 'true' : 'false'),
      onClick: (e, el) => el.call('phoneNavigationPanel', 'apps')
    },
    PhoneNavButton_page: {
      icon: 'sidebarLeft',
      label: 'shell.phoneNav.page',
      hide: (el, s) => !el.call('phoneNavigationPageRail', s.root),
      ariaLabel: (el) => el.call('polyglot', 'shell.phoneNav.pageMenu'),
      ariaHaspopup: 'dialog',
      ariaExpanded: (el, s) =>
        s.root[el.call('phoneNavigationPageRail', s.root)] ? 'true' : 'false',
      onClick: (e, el) => el.call('phoneNavigationBrowse')
    },
    PhoneNavButton_ai: {
      icon: 'chat',
      label: 'shell.phoneNav.assistant',
      ariaLabel: (el) => el.call('polyglot', 'shell.phoneNav.assistantAction'),
      AppGlyphPlate: {
        background: 'highlight',
        color: 'title-reversed'
      },
      hide: (el, s) =>
        !el.call('featureEnabled', s.root, 'aiAssistant') ||
        !el.call('featureEnabled', s.root, 'aiAssistant.composer'),
      ariaPressed: (el, s) =>
        s.root._phoneComposerOpen || (s.root.assistantOpen && s.root.assistantDock !== 'minimized')
          ? 'true'
          : 'false',
      onClick: (e, el) => el.call('phoneNavigationAssistant', el.parent.parent.canCompose)
    },
    PhoneNavButton_menu: {
      icon: 'burgerMenu',
      label: 'shell.phoneNav.menu',
      ariaHaspopup: 'dialog',
      ariaExpanded: (el, s) => (s.root._phoneNavPanel === 'menu' ? 'true' : 'false'),
      // The phone hides the header bell (functions/phoneNavigation.js
      // phoneNavigationStyles). The menu holds the inbox, so this button
      // carries the unread signal: a dot for the eye, the count for a screen
      // reader. The realtime push writes `notifs` after the dock mounted:
      // each element that reads it subscribes to it, as the header bell and
      // the mail dot do.
      subscribeTo: ['notifs'],
      ariaDescription: (el, s) => {
        const n = el.call('phoneNavigationUnread', s.root)
        return n
          ? el.call('polyglot', 'shell.notifications.tab.unreadCount').replace('{count}', n)
          : null
      },
      AppGlyphPlate: {
        // The unread dot: the shell's dot geometry (the navbar's and the mail
        // button's) on the brand's alert plate, the one the comm inbox tab's
        // count wears (comm/components.js CommNavTab), never a raw hue.
        UnreadBadge: {
          tag: 'span',
          subscribeTo: ['notifs'],
          hide: (el, s) => !el.call('phoneNavigationUnread', s.root),
          position: 'absolute',
          top: '0',
          right: '0',
          width: 'Y',
          height: 'Y',
          round: 'radiusPill',
          theme: 'alert',
          pointerEvents: 'none'
        }
      },
      onClick: (e, el) => el.call('phoneNavigationPanel', 'menu')
    }
  },

  AppDialog: {
    stateKey: '_phoneNavPanel',
    closeCall: 'phoneNavigationClose',
    position: 'fixed',
    zIndex: '2000',
    // The panel takes focus, never a field: a focused field raises the
    // phone keyboard over the tiles the member came to pick from.
    initialFocus: 'panel',
    panelSize: 'sm',
    Panel: {
      dataPhonePanel: (el, s) => s.root._phoneNavPanel || null,
      '&[data-phone-panel="apps"]': {
        height: '88dvh',
        maxHeight: 'calc(100dvh - env(safe-area-inset-top) - var(--spacing-A))'
      },
      AppDialogHead: {
        titleId: 'phoneNavigationTitle',
        heading: (el, s) =>
          s.root._phoneNavPanel === 'apps'
            ? 'shell.phoneNav.appsTitle'
            : 'shell.phoneNav.menuTitle',
        subtitle: (el, s) =>
          s.root._phoneNavPanel === 'apps' ? 'shell.phoneNav.appsHint' : 'shell.phoneNav.menuHint'
      },
      // Search stays within reach while the directory scrolls. Opening the
      // sheet itself never focuses a field or raises the phone keyboard.
      PhoneSheetAction_search: {
        hide: (el, s) => s.root._phoneNavPanel !== 'apps',
        icon: 'search',
        label: 'shell.headerSearch.placeholder',
        margin: '0 A A',
        flexShrink: '0',
        onClick: (e, el) => el.call('phoneNavigationSearch')
      },
      AppDialogBody: {
        Apps: {
          hide: (el, s) => s.root._phoneNavPanel !== 'apps',
          flow: 'y',
          gap: 'A',
          minWidth: '0',
          Sections: {
            flow: 'y',
            gap: 'B',
            minWidth: '0',
            childExtends: 'PhoneAppSection',
            childrenAs: 'state',
            children: (el, s) =>
              s.root._phoneNavPanel === 'apps' ? el.call('phoneNavigationAppGroups', s.root) : []
          }
        },
        Menu: {
          tag: 'div',
          hide: (el, s) => s.root._phoneNavPanel !== 'menu',
          flow: 'y',
          gap: 'X',
          minWidth: '0',
          AppOrgSwitcher: {
            hide: (el, s) => !!s.root.isDemo,
            marginBottom: 'Z'
          },
          PhoneSheetAction_search: {
            icon: 'search',
            label: 'shell.headerSearch.placeholder',
            onClick: (e, el) => el.call('phoneNavigationSearch')
          },
          // The header bell's place on a phone (persona round 4, marcus at
          // 390 px, dev 2026-10-28: a new notification left no trace). The
          // row opens the inbox where the bell's "View all" opens it: the
          // shell's window (`preferSlot`); a host without one (comm) opens
          // it as the page.
          PhoneSheetAction_inbox: {
            icon: 'bell',
            label: 'shell.app.inbox',
            // The row's meta count (AppSidebarRow), the bell's own number.
            Meta: {
              subscribeTo: ['notifs'],
              text: (el, s) => {
                const n = el.call('phoneNavigationUnread', s.root)
                return n > 99 ? '99+' : String(n)
              },
              hide: (el, s) => !el.call('phoneNavigationUnread', s.root)
            },
            onClick: (e, el) => {
              el.call('phoneNavigationClose')
              el.call('openAppPage', '/inbox', { preferSlot: true })
            }
          },
          PhoneSheetAction_account: {
            icon: 'user',
            label: 'shell.phoneNav.account',
            onClick: (e, el) => {
              const trigger = el.lookup('PhoneNavigation')?.Dock?.PhoneNavButton_menu || el
              el.call('phoneNavigationClose')
              el.call('openAccountMenu', trigger)
            }
          },
          FlyTools: {
            subscribeTo: ['assistantMode', '_aiFly'],
            hide: (el, s) =>
              s.root.assistantMode !== 'fly' || !s.root._aiFly || !s.root._aiFly.open,
            flow: 'y',
            gap: 'X',
            // Fly's canvas actions as rows of this menu (the menu closes
            // first). Add section lives in the composer's "+" menu, on a phone
            // as everywhere (Nika 2026-10-23); the apps are this menu's own
            // rows.
            PhoneSheetAction_new: {
              icon: 'plus',
              label: 'workspace.assistantFly.new',
              onClick: (e, el) => {
                el.call('phoneNavigationClose')
                el.call('assistantFlyWorkspaceNew')
              }
            },
            PhoneSheetAction_close: {
              icon: 'x',
              label: 'workspace.assistantFly.close',
              onClick: (e, el) => {
                el.call('phoneNavigationClose')
                el.call('assistantFlyWorkspaceDismiss')
              }
            }
          }
        },
        Error: {
          role: 'alert',
          color: 'caption',
          fontSize: 'Z',
          text: (el, s) =>
            s.root._phoneNavError ? el.call('polyglot', 'shell.flyWorkspace.error.navigation') : ''
        }
      }
    }
  }
}

// One dock control: the glyph leads, the name sits under it. Quiet at rest;
// the page you are on, an open sheet and the raised composer take the
// `selected` wash and `title` ink (brand interaction law).
export const PhoneNavButton = {
  extends: 'AppCapsuleBtnGhost',
  flex: '1 1 0%',
  minWidth: '0',
  '&[data-control-size="regular"]': { minHeight: 'thumbnailXs' },
  touchAction: 'manipulation',
  flow: 'y',
  justifyContent: 'flex-start',
  gap: 'X',
  // Keep the full track for the name, including on a narrow five-action dock.
  padding: 'X 0',
  fontSize: 'A',
  round: 'radiusControl',
  color: 'caption',
  '&[aria-current="page"],&[aria-expanded="true"],&[aria-pressed="true"]': {
    background: 'selected',
    color: 'title'
  },
  Icon: null,
  AppGlyphPlate: {
    boxSize: 'avatarSm',
    minWidth: 'avatarSm',
    borderWidth: '0',
    color: 'inherit',
    round: 'radiusPill',
    ariaHidden: true,
    Icon: { name: (el) => el.parent.parent.icon, boxSize: 'iconMd' },
    Dot: null
  },
  // Names can grow with the member's text preference. The glyphs stay on
  // one baseline while the dock's measured clearance follows a wrapped name.
  Label: {
    tag: 'span',
    text: (el) => el.call('polyglot', el.parent.label),
    fontSize: 'Y',
    lineHeight: '1.1',
    maxWidth: '100%',
    overflow: 'visible',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere'
  }
}

// The same tactile row for search, account and canvas actions. Labels wrap
// instead of clipping when the member changes language or increases text size.
export const PhoneSheetAction = {
  extends: 'AppSidebarRow',
  minHeight: 'thumbnailXs',
  '@mobileL': { minHeight: 'thumbnailXs' },
  '@coarse': { minHeight: 'thumbnailXs' },
  padding: 'Z A',
  gap: 'A',
  background: 'widgetPlate',
  color: 'title',
  Icon: { boxSize: 'iconMd' },
  Label: { whiteSpace: 'normal', overflow: 'visible', lineHeight: '1.4' },
  Icon_next: {
    name: 'chevronRight',
    boxSize: 'iconSm',
    flexShrink: '0',
    color: 'caption',
    ariaHidden: true
  }
}

// The launcher's own section and tile (components/SearchAppsMenu.js), held by
// the phone sheet: a pick runs the dock's guarded opener, which closes the
// sheet, instead of the header dropdown's close and switcher.
export const PhoneAppSection = {
  extends: 'SearchAppsSection',
  SasGrid: {
    childExtends: 'PhoneAppTile',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 'Y',
    justifyItems: 'stretch',
    '@mobileXS': {
      gridTemplateColumns: 'minmax(0, 1fr)',
      '& [data-launcher-tile]': { gridColumn: '1 / -1' }
    }
  }
}

export const PhoneAppTile = {
  extends: 'SearchAppTile',
  // A dialog row, not a menu item: the sheet is no menu.
  role: null,
  // A row has less label room than the desktop springboard. Long names and
  // long single words take the full line instead of splitting into fragments.
  dataSpan: (el, s) => {
    const name = String(el.call('storeItemText', { key: s.appKey, name: s.title }, 'name') || '')
    return [...name].length > 28 || name.split(/\s+/).some((word) => [...word].length > 12)
      ? 'row'
      : null
  },
  // No desktop drag/context-menu hints on a touch directory. A long press
  // stays native scrolling/selection, and a tap uses the guarded opener.
  draggable: false,
  title: null,
  'aria-keyshortcuts': null,
  'aria-label': (el, s) => el.call('storeItemText', { key: s.appKey, name: s.title }, 'name'),
  onDragstart: null,
  onDragend: null,
  onContextmenu: null,
  onKeydown: null,
  flow: 'x',
  justifyContent: 'flex-start',
  width: '100%',
  minHeight: 'thumbnailSm',
  '&[data-control-size="regular"]': { minHeight: 'thumbnailSm' },
  padding: 'Z',
  gap: 'Z',
  background: 'widgetPlate',
  round: 'radiusControl',
  IconTile: {
    boxSize: 'avatarSm',
    minWidth: 'avatarSm',
    borderWidth: '0',
    Icon: { boxSize: 'iconMd' }
  },
  Label: { flex: '1', textAlign: 'left', fontSize: 'Z', lineHeight: '1.35' },
  // One open at a time: the tiles rest while the guarded opener runs.
  disabled: (el, s) => !!s.root?._phoneNavOperation,
  onClick: (e, el, s) => {
    if (s.route) el.call('phoneNavigationOpen', s.route)
  }
}
